import { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { FaInstagram } from 'react-icons/fa';
import { useScrollPosition } from '../../hooks/useScrollPosition';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { navigationItems } from '../../data/navigation';
import logo from '../../assets/images/brand/NEWNEWLOGO.png';
import './Navbar.css';

export function Navbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const scrollPos = useScrollPosition();
  const isMobile = useMediaQuery('(max-width: 768px)');

  const hasScrolled = scrollPos.y > 10;

  // Close menu when navigating
  const handleNavLinkClick = () => {
    setIsMenuOpen(false);
  };

  // Close menu when resizing to desktop
  useEffect(() => {
    if (!isMobile && isMenuOpen) {
      setIsMenuOpen(false);
    }
  }, [isMobile, isMenuOpen]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  return (
    <nav className={`navbar ${hasScrolled ? 'navbar--scrolled' : ''}`}>
      <div className="navbar-content">
        {/* Logo */}
        <NavLink to="/" className="navbar-logo">
          <img src={logo} alt="Mims Family Painting" />
        </NavLink>

        {/* Desktop Navigation — Primary pages centered */}
        {!isMobile && (
          <ul className="navbar-links">
            {navigationItems
              .filter((item) => !['/about', '/contact'].includes(item.path))
              .map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
          </ul>
        )}

        {/* Right side: Secondary pages + Instagram + Mobile menu toggle */}
        <div className="navbar-actions">
          {!isMobile && (
            <ul className="navbar-secondary">
              {navigationItems
                .filter((item) => ['/about', '/contact'].includes(item.path))
                .map((item) => (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      className={({ isActive }) => `nav-link nav-link--secondary ${isActive ? 'active' : ''}`}
                    >
                      {item.label}
                    </NavLink>
                  </li>
                ))}
            </ul>
          )}

          <a
            href="https://www.instagram.com/mimspainting"
            target="_blank"
            rel="noopener noreferrer"
            className="navbar-instagram"
            aria-label="Visit our Instagram"
          >
            <FaInstagram />
          </a>

          {isMobile && (
            <button
              className={`hamburger ${isMenuOpen ? 'active' : ''}`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
            >
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
            </button>
          )}
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobile && isMenuOpen && (
        <div className="mobile-menu-overlay">
          <ul className="mobile-menu-links">
            {navigationItems
              .filter((item) => !['/about', '/contact'].includes(item.path))
              .map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`}
                    onClick={handleNavLinkClick}
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
          </ul>

          <ul className="mobile-menu-secondary">
            {navigationItems
              .filter((item) => ['/about', '/contact'].includes(item.path))
              .map((item) => (
                <li key={item.path}>
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => `mobile-nav-link mobile-nav-link--secondary ${isActive ? 'active' : ''}`}
                    onClick={handleNavLinkClick}
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
          </ul>
        </div>
      )}
    </nav>
  );
}
